import { useEffect, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { ConfirmDialog } from "../../shared/ui/ConfirmDialog"
import { CopyableId } from "../../shared/ui/CopyableId"
import {
  getNotificationApi,
  deleteNotificationApi,
  NotificationCampaign,
} from "../../features/notifications/api/notifications.api"
import {
  ChevronLeftIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  CalendarIcon,
  TrashIcon,
  MegaphoneIcon,
} from "@heroicons/react/16/solid"

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })

const StatCard = ({ label, value, className }: { label: string; value: number; className: string }) => (
  <div className={`rounded-xl border p-4 ${className}`}>
    <p className="text-2xl font-bold tabular-nums">{value}</p>
    <p className="text-xs mt-0.5 opacity-70">{label}</p>
  </div>
)

export const NotificationDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [item, setItem] = useState<NotificationCampaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getNotificationApi(id)
      .then((res) => setItem(res.data))
      .catch(() => setItem(null))
      .finally(() => setLoading(false))
  }, [id])

  const handleDelete = async () => {
    if (!id) return
    setDeleting(true)
    try {
      await deleteNotificationApi(id)
      navigate("/admin/notifications/history")
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4 animate-pulse">
        <div className="h-32 rounded-xl bg-gray-100" />
        <div className="h-48 rounded-xl bg-gray-100" />
      </div>
    )
  }

  if (!item) {
    return <div className="py-20 text-center text-gray-400">Рассылка не найдена</div>
  }

  const filterEntries = Object.entries(item.filters).filter(([, v]) => v !== undefined && v !== null && v !== "")

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Link
        to="/admin/notifications/history"
        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors"
      >
        <ChevronLeftIcon className="w-3.5 h-3.5" />
        К истории рассылок
      </Link>

      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <MegaphoneIcon className="w-5 h-5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 truncate">{item.title}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{item.body}</p>
            </div>
          </div>
          <button
            onClick={() => setDeleteOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            Убрать из истории
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t text-xs text-gray-400">
          <span className="flex items-center gap-1.5"><IdentificationIcon className="w-3.5 h-3.5" />{item.created_by_name}</span>
          <span className="flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5" />{formatDate(item.created_at)}</span>
          <CopyableId value={item.id} length={16} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="аудитория" value={item.target_count} className="bg-gray-50 border-gray-100 text-gray-700" />
        <StatCard label="доставлено" value={item.sent_count} className="bg-emerald-50 border-emerald-100 text-emerald-700" />
        <StatCard label="не доставлено" value={item.failed_count} className="bg-red-50 border-red-100 text-red-600" />
      </div>

      <div className="bg-white rounded-xl border p-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-gray-400" />
          Фильтры сегмента
        </h2>
        {filterEntries.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Все пользователи (без фильтров)</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {filterEntries.map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full capitalize">
                {k}: <span className="font-medium">{String(v)}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {item.target_count > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Доставляемость</h2>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
            <div className="h-full bg-emerald-500" style={{ width: `${(item.sent_count / item.target_count) * 100}%` }} />
            <div className="h-full bg-red-400" style={{ width: `${(item.failed_count / item.target_count) * 100}%` }} />
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1.5 text-emerald-600"><CheckCircleIcon className="w-3.5 h-3.5" />{((item.sent_count / item.target_count) * 100).toFixed(0)}% доставлено</span>
            {item.failed_count > 0 && (
              <span className="flex items-center gap-1.5 text-red-500"><ExclamationTriangleIcon className="w-3.5 h-3.5" />{((item.failed_count / item.target_count) * 100).toFixed(0)}% не долетело</span>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Убрать из истории?"
        message="Запись будет удалена из истории. Саму рассылку это не отменяет — она уже отправлена."
        confirmLabel="Убрать"
        loading={deleting}
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  )
}
